import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useParams,
  useNavigate,
  useLocation,
  matchPath,
} from 'react-router-dom';
import {
  Container,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
} from 'reactstrap';
import PageBreadcrumb from '../../../../Components/Common/PageBreadcrumb';
import classnames from 'classnames';
import useProcurementDetail from '../../../../shared/offer-domain/procurement/useProcurementDetail';
import useChangeEvaluationStatus from '../../hooks/useChangeEvaluationStatus';
import useClosingEvaluationStatus from '../../hooks/useClosingEvaluationStatus';
import useSubmittedBidder from '../../hooks/useSubmittedBidder';
import EvaluationOperational from './EvaluationOperational';
import Clarification from './Clarification';
import Improvement from './Improvement';
import Negotiation from './Negotiation';
import ClarificationDetail from './Clarification/ClarificationDetail';
import { MAIN_TABS } from './constants/tabs';
import '../../TenderEvaluation.css';
import useProcurementDocuments from '../../hooks/useProcurementDocuments';
import { useQuantityDeliveries } from '../../../../Components/QuantityTable';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../shared/offer-domain/improvement';
import { SubmitConfirmModal } from '../../../../shared/offer-domain/ui';
import {
  displayValue,
  flattenSubmittedBidderDeliveryDetails,
  formatPeriodRange,
  formatProposalDeadline,
  formatRemainingCountdown,
  getContractTypeLabel,
  getTenderEvaluationStatusConfig,
  getEvaluationOperationalLockReason,
  canAccessEvaluationOperationalData,
  canMutateEvaluationOperationalData,
  hasSubmittedImprovementOffers,
  isEvaluationSaved,
  isEvaluationUnderEvaluation,
  isHpsAvailable,
} from '../../utils/tenderEvaluationHelpers';
import {
  areAllBiddersClarificationDone,
  formatSubmittedDate,
} from '../../../../shared/offer-domain/clarification';
import {
  getVisibleProcurementClauses,
  useClarificationUnreadByMitra,
} from '../../../../shared/offer-domain/respond';
import ActionButton from '../../../../Components/Common/ActionButton';
import { confirmAction } from '../../../../utils/confirm';
import { DetailHeader } from '../../../../Components/Common/Detail';
import StatusBadge from '../../../../Components/Common/StatusBadge';

const CLARIFICATION_DETAIL_PATH =
  '/apps-tenderevaluation/dtl/:id/clarification/:procurementMitraId';

const DetailPage = ({
  initialMainTab = 'evaluation-operational',
  procurement,
  isLoading,
  refresh,
  unreadRefreshKey = 0,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState(initialMainTab);
  const [visitedMainTabs, setVisitedMainTabs] = useState({
    [initialMainTab]: true,
  });
  const [activeTab, setActiveTab] = useState('Offer');
  const [offerTabVisited, setOfferTabVisited] = useState(false);
  const [improvementClosingDate, setImprovementClosingDate] = useState(null);
  const [lockModalMode, setLockModalMode] = useState(null);
  const [lockGateMessage, setLockGateMessage] = useState('');
  const [isLockChecking, setIsLockChecking] = useState(false);
  const improvementClosingDateRef = useRef(improvementClosingDate);
  improvementClosingDateRef.current = improvementClosingDate;
  const appliedSavedDefaultTabRef = useRef(false);
  const { documents: procurementDocuments, isLoading: isLoadingDocuments } =
    useProcurementDocuments(id, {
      enabled: Boolean(visitedMainTabs['evaluation-operational']),
      isActive: activeMainTab === 'evaluation-operational',
    });
  const {
    parcels,
    isLoading: isLoadingParcels,
    error: parcelsError,
    refresh: refreshParcels,
  } = useQuantityDeliveries(id, {
    enabled: Boolean(
      visitedMainTabs['evaluation-operational'] ||
      visitedMainTabs.improvement ||
      visitedMainTabs.negotiation
    ),
    notifyError: true,
  });
  const { changeStatus, isLoading: isChangingStatus } =
    useChangeEvaluationStatus();
  const { closeEvaluation, isLoading: isClosingEvaluation } =
    useClosingEvaluationStatus();

  const evaluationLockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessEvaluationData =
    canAccessEvaluationOperationalData(procurement);
  const canMutateEvaluation = canMutateEvaluationOperationalData(procurement);
  const evaluationSaved = isEvaluationSaved(procurement);
  const evaluationUnderEvaluation = isEvaluationUnderEvaluation(procurement);
  const submissionDeadline = procurement?.proposalSubmissionDeadline ?? null;
  const [submissionRemainingSeconds, setSubmissionRemainingSeconds] =
    useState(0);

  useEffect(() => {
    if (evaluationLockReason !== 'awaiting' || !submissionDeadline) {
      setSubmissionRemainingSeconds(0);
      return undefined;
    }

    const tick = () => {
      const deadlineMs = new Date(submissionDeadline).getTime();
      if (!Number.isFinite(deadlineMs)) {
        setSubmissionRemainingSeconds(0);
        return;
      }
      setSubmissionRemainingSeconds(
        Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000))
      );
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [evaluationLockReason, submissionDeadline]);

  const visibleMainTabs = useMemo(
    () =>
      evaluationSaved
        ? MAIN_TABS
        : MAIN_TABS.filter(tab => tab.id !== 'negotiation'),
    [evaluationSaved]
  );

  const { items: submittedBidders, refresh: refreshSubmittedBidders } =
    useSubmittedBidder(id, {
      enabled: Boolean(
        id &&
        (canAccessEvaluationData ||
          visitedMainTabs.clarification ||
          visitedMainTabs.improvement ||
          visitedMainTabs.negotiation)
      ),
      isActive: Boolean(canAccessEvaluationData),
      refreshToken: unreadRefreshKey,
    });

  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );
  const mitraIds = useMemo(
    () => (submittedBidders ?? []).map(bidder => bidder.id).filter(Boolean),
    [submittedBidders]
  );
  const { unreadByMitraId, hasAnyUnread: hasClarificationUnread } =
    useClarificationUnreadByMitra({
      procurementId: id,
      mitraIds,
      clauseDetailIds,
      isEvaluatorView: true,
      enabled: Boolean(id && mitraIds.length),
      refreshKey: unreadRefreshKey,
    });

  useEffect(() => () => toast.dismiss(), []);

  useEffect(() => {
    setVisitedMainTabs(prev =>
      prev[activeMainTab] ? prev : { ...prev, [activeMainTab]: true }
    );
  }, [activeMainTab]);

  useEffect(() => {
    if (activeMainTab === 'evaluation-operational' && activeTab === 'Offer') {
      setOfferTabVisited(true);
    }
  }, [activeMainTab, activeTab]);

  useEffect(() => {
    if (
      (activeMainTab !== 'clarification' &&
        activeMainTab !== 'improvement' &&
        activeMainTab !== 'negotiation') ||
      !id
    ) {
      return;
    }
    refreshSubmittedBidders();
  }, [activeMainTab, id, refreshSubmittedBidders]);

  useEffect(() => {
    if (!evaluationSaved && activeMainTab === 'negotiation') {
      setActiveMainTab('evaluation-operational');
    }
  }, [evaluationSaved, activeMainTab]);

  useEffect(() => {
    if (!evaluationSaved || appliedSavedDefaultTabRef.current) return;
    appliedSavedDefaultTabRef.current = true;
    if (initialMainTab === 'clarification') return;
    setActiveMainTab('negotiation');
    setVisitedMainTabs(prev =>
      prev.negotiation ? prev : { ...prev, negotiation: true }
    );
  }, [evaluationSaved, initialMainTab]);

  const statusConfig = getTenderEvaluationStatusConfig(
    procurement?.evaluationStatusWording
  );
  const categoryLabel =
    procurement?.procurementTypeHeaderLabel ||
    procurement?.productType?.product?.name ||
    'Product Hydro';

  const closingDateFromBidders = useMemo(() => {
    const rows = flattenSubmittedBidderDeliveryDetails(submittedBidders);
    return resolveImprovementClosingDate(rows);
  }, [submittedBidders]);

  const effectiveClosingDate =
    improvementClosingDate ?? closingDateFromBidders ?? null;

  const handleStartEvaluation = async () => {
    if (!procurement?.id) return;

    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Start tender evaluation?',
      message:
        'The evaluation process starts and the bidding documents become accessible for evaluation.',
      subject: procurement?.procurementNumber,
      confirmLabel: 'Start evaluation',
    });
    if (!confirmed) return;

    await changeStatus({
      procurementId: procurement.id,
      newStatus: 3,
      onSuccess: refresh,
    });
  };

  const handleOpenLockModal = async () => {
    if (isLockChecking || isClosingEvaluation || evaluationSaved) return;
    setIsLockChecking(true);
    try {
      setVisitedMainTabs(prev => ({
        ...prev,
        clarification: true,
        improvement: true,
      }));

      const bidders =
        (await refreshSubmittedBidders()) ?? submittedBidders ?? [];
      const closingDate =
        improvementClosingDateRef.current ??
        resolveImprovementClosingDate(
          flattenSubmittedBidderDeliveryDetails(bidders)
        ) ??
        null;
      const clarificationDone = areAllBiddersClarificationDone(bidders);
      const hasRequestedImprovement = hasSubmittedImprovementOffers(bidders);
      const improvementEnded =
        Boolean(closingDate) && !isImprovementClosingActive(closingDate);

      const missing = [];
      if (!offerTabVisited) {
        missing.push(
          'Open Evaluation Operational > Offer and review offer data before locking.'
        );
      }
      if (!hasRequestedImprovement) {
        missing.push(
          'At least one improvement offer must be Requested before locking.'
        );
      }
      if (!clarificationDone) {
        missing.push(
          'All submitted bidders must have Closing Clarification status.'
        );
      }
      if (!improvementEnded) {
        missing.push(
          closingDate
            ? `Improvement must have ended (still active until ${formatImprovementClosingDate(closingDate)}).`
            : 'Improvement must have ended before locking.'
        );
      }
      if (!isHpsAvailable(procurement)) {
        missing.push(
          'HPS data is not ready to lock yet. If a correction is still open, use the HPS tab to ask the HPS team to update it first.'
        );
      }

      if (missing.length) {
        setLockGateMessage(
          `Unable to lock evaluation yet:\n• ${missing.join('\n• ')}`
        );
        setLockModalMode('blocked');
        return;
      }

      setLockGateMessage(
        'Once locked, Evaluation Operational and Clarification become read-only. Negotiation selection opens in a new tab.'
      );
      setLockModalMode('confirm');
    } finally {
      setIsLockChecking(false);
    }
  };

  const handleConfirmLock = async () => {
    if (!procurement?.id) return;
    const success = await closeEvaluation({
      procurementId: procurement.id,
    });
    if (!success) return;
    await refresh?.();
    setLockModalMode(null);
    setVisitedMainTabs(prev => ({ ...prev, negotiation: true }));
    setActiveMainTab('negotiation');
  };

  const getEvaluationAlertConfig = () => {
    if (evaluationSaved) {
      return {
        color: 'secondary',
        icon: 'ri-lock-line',
        title: 'Saved Evaluation',
        desc: 'Evaluation is locked. Evaluation Operational and Clarification are read-only. Select improvement offers for negotiation in the Negotiation tab.',
      };
    }
    if (evaluationLockReason === 'awaiting') {
      const deadlineLabel = formatProposalDeadline(submissionDeadline);
      const hasDeadline = Boolean(submissionDeadline) && deadlineLabel !== '-';
      const isExpired = hasDeadline && submissionRemainingSeconds <= 0;
      return {
        color: 'danger',
        icon: 'ri-information-line',
        title: 'Belum dapat Dimulai',
        desc: 'Dikarenakan proses tender belum memasuki waktu closing date',
        action: hasDeadline ? (
          <div
            className={['te-alert-timer', isExpired ? 'is-expired' : '']
              .filter(Boolean)
              .join(' ')}
            title={`Submission Deadline ${deadlineLabel} WIB`}
          >
            <i className='ri-timer-line' aria-hidden='true' />
            <div>
              <span className='te-alert-timer__label'>Timer</span>
              <strong className='te-alert-timer__value'>
                {isExpired
                  ? 'Expired'
                  : formatRemainingCountdown(submissionRemainingSeconds)}
              </strong>
              <span className='te-alert-timer__deadline'>
                {deadlineLabel} WIB
              </span>
            </div>
          </div>
        ) : null,
      };
    }
    if (evaluationUnderEvaluation) {
      return {
        color: 'success',
        icon: 'ri-check-line',
        title: 'Evaluasi Sedang Berlangsung',
        desc: 'Proses evaluasi tender telah dimulai. Anda dapat mengakses dokumen penawaran untuk melanjutkan evaluasi.',
        action: (
          <ActionButton
            variant='primary'
            icon='ri-lock-line'
            className='text-nowrap'
            loading={isClosingEvaluation || isLockChecking}
            onClick={handleOpenLockModal}
          >
            {isClosingEvaluation || isLockChecking
              ? 'Processing...'
              : 'Lock/Saved Evaluation'}
          </ActionButton>
        ),
      };
    }
    return {
      color: 'primary',
      icon: 'ri-information-line',
      title: 'Mulai Proses Evaluasi',
      desc: 'Klik tombol di samping untuk memulai dan untuk mengakses dokumen penawaran',
      action: (
        <ActionButton
          variant='primary'
          icon='ri-play-circle-line'
          className='text-nowrap'
          loading={isChangingStatus}
          disabled={!procurement?.id}
          onClick={handleStartEvaluation}
        >
          {isChangingStatus ? 'Processing...' : 'Start Tender Evaluation'}
        </ActionButton>
      ),
    };
  };

  const evaluationAlertConfig = getEvaluationAlertConfig();

  return (
    <div className='page-content'>
      <Container fluid>
        <PageBreadcrumb
          fallback={{
            label: 'Tender Evaluation',
            to: '/apps-tenderevaluation/view',
          }}
          current={`Detail # ${displayValue(procurement?.procurementNumber)}`}
          listClassName='p-3 py-2 bg-light'
        />

        <DetailHeader
          loading={isLoading && !procurement}
          onBack={() => navigate('/apps-tenderevaluation/view')}
          backLabel='Back to list'
          eyebrow={
            <>
              <i className='ri-hashtag' aria-hidden='true' />
              <span>{displayValue(procurement?.procurementNumber)}</span>
              <span className='opacity-50'>|</span>
              <span>{categoryLabel}</span>
            </>
          }
          title={displayValue(procurement?.titleProcurement)}
          badges={
            <StatusBadge tone={statusConfig.tone} label={statusConfig.label} />
          }
          alert={
            <Alert
              color={evaluationAlertConfig.color}
              className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-0'
            >
              <div className='d-flex align-items-start gap-2'>
                <i className={`${evaluationAlertConfig.icon} fs-18 mt-1`}></i>
                <div>
                  <h5 className='alert-heading fs-14 fw-bold mb-1'>
                    {evaluationAlertConfig.title}
                  </h5>
                  <p className='mb-0 fs-13'>{evaluationAlertConfig.desc}</p>
                </div>
              </div>
              {evaluationAlertConfig.action}
            </Alert>
          }
          facts={[
            {
              icon: 'ri-calendar-event-line',
              label: 'Submitted',
              value: formatSubmittedDate(
                procurement?.submittedDateTenderOpening
              ),
            },
            {
              icon: 'ri-repeat-line',
              label: 'Contract',
              value: getContractTypeLabel(procurement?.contractType),
            },
            {
              icon: 'ri-calendar-2-line',
              label: 'Period',
              value: formatPeriodRange(procurement),
            },
            {
              icon: 'ri-team-line',
              label: 'Submitted bidders',
              value: submittedBidders ? String(submittedBidders.length) : null,
            },
          ]}
        />

        <div>
          <Card className='shadow-none border'>
            <CardBody className='p-0'>
              <Nav tabs className='nav-tabs-custom nav-primary px-3 pt-3'>
                {visibleMainTabs.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      tag='button'
                      type='button'
                      className={classnames('cursor-pointer', {
                        active: activeMainTab === tab.id,
                      })}
                      onClick={() => setActiveMainTab(tab.id)}
                    >
                      <i className={`${tab.icon} align-middle me-1`}></i>
                      <span
                        className={
                          tab.id === 'clarification' && hasClarificationUnread
                            ? 'fw-semibold text-info'
                            : 'fw-semibold'
                        }
                      >
                        {tab.label}
                      </span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeMainTab} className='p-3 p-md-4'>
                {visitedMainTabs['evaluation-operational'] && (
                  <TabPane tabId='evaluation-operational'>
                    <EvaluationOperational
                      activeTab={activeTab}
                      onChangeTab={setActiveTab}
                      procId={id}
                      procurement={procurement}
                      isLoadingProcurement={isLoading}
                      onRefreshProcurement={refresh}
                      parcels={parcels}
                      isLoadingParcels={isLoadingParcels}
                      procurementDocuments={procurementDocuments}
                      isLoadingDocuments={isLoadingDocuments}
                      readOnly={!canMutateEvaluation}
                      improvementClosingDate={effectiveClosingDate}
                    />
                  </TabPane>
                )}

                {visitedMainTabs.clarification && (
                  <TabPane tabId='clarification'>
                    <Clarification
                      procurementId={id}
                      procurement={procurement}
                      isLoadingProcurement={isLoading}
                      unreadByMitraId={unreadByMitraId}
                      isActive={activeMainTab === 'clarification'}
                      refreshToken={unreadRefreshKey}
                      readOnly={!canMutateEvaluation}
                    />
                  </TabPane>
                )}

                {visitedMainTabs.improvement && (
                  <TabPane tabId='improvement'>
                    <Improvement
                      procurement={procurement}
                      procId={id}
                      parcels={parcels}
                      isLoadingParcels={isLoadingParcels}
                      isLoadingProcurement={isLoading}
                      closingDate={effectiveClosingDate}
                      onImprovementClosingDateChange={setImprovementClosingDate}
                    />
                  </TabPane>
                )}

                {visitedMainTabs.negotiation && evaluationSaved && (
                  <TabPane tabId='negotiation'>
                    <Negotiation
                      procurement={procurement}
                      procId={id}
                      parcels={parcels}
                      isLoadingParcels={isLoadingParcels}
                      parcelsError={parcelsError}
                      onRetryParcels={refreshParcels}
                      isLoadingProcurement={isLoading}
                      isActive={activeMainTab === 'negotiation'}
                    />
                  </TabPane>
                )}
              </TabContent>
            </CardBody>
          </Card>
        </div>
      </Container>

      <SubmitConfirmModal
        isOpen={lockModalMode === 'blocked'}
        onClose={() => setLockModalMode(null)}
        title='Unable to Lock Evaluation'
        message={lockGateMessage}
        cancelLabel='Close'
        showConfirm={false}
      />
      <SubmitConfirmModal
        isOpen={lockModalMode === 'confirm'}
        onClose={() => setLockModalMode(null)}
        onConfirm={handleConfirmLock}
        title='Lock evaluation?'
        message={lockGateMessage}
        confirmLabel='Lock evaluation'
        isSubmitting={isClosingEvaluation}
      />
    </div>
  );
};

const TenderEvaluationDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const clarificationMatch = matchPath(
    { path: CLARIFICATION_DETAIL_PATH, end: true },
    location.pathname
  );
  const { procurement, isLoading, refresh } = useProcurementDetail(id);
  const wasClarificationDetailRef = useRef(Boolean(clarificationMatch));
  const [unreadRefreshKey, setUnreadRefreshKey] = useState(0);

  useEffect(() => {
    const isClarificationDetail = Boolean(clarificationMatch);
    if (wasClarificationDetailRef.current && !isClarificationDetail && id) {
      setUnreadRefreshKey(key => key + 1);
    }
    wasClarificationDetailRef.current = isClarificationDetail;
  }, [clarificationMatch, id]);

  return (
    <>
      <div className={clarificationMatch ? 'd-none' : undefined}>
        <DetailPage
          initialMainTab={
            clarificationMatch ? 'clarification' : 'evaluation-operational'
          }
          procurement={procurement}
          isLoading={isLoading}
          refresh={refresh}
          unreadRefreshKey={unreadRefreshKey}
        />
      </div>
      {clarificationMatch ? (
        <ClarificationDetail
          key={clarificationMatch.params.procurementMitraId}
          procurementId={clarificationMatch.params.id}
          offerId={clarificationMatch.params.procurementMitraId}
          procurement={procurement}
          isLoadingProcurement={isLoading}
        />
      ) : null}
    </>
  );
};

export default TenderEvaluationDetail;
